import {
  EMPLOYER_CONTACT_US_URL,
  CUSTOMER_SERVICE_HOURS,
  EMPLOYER_HELP_CENTRE_URL,
} from '@seek/shared-constants';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Hidden,
  Columns,
  Column,
  ContentBlock,
  Heading,
  Text,
  Stack,
  TextLink,
} from 'braid-design-system';

import { useConfig } from 'src/hooks/context';
import { useHygraphRichText } from 'src/hooks/use-hygraph-rich-text';
import {
  SHOPFRONT_LANDING_PAGE_CONTACT_US,
  SHOPFRONT_LANDING_PAGE_HELP_CENTRE,
} from 'src/modules/ApiClient/cms/richTextConstants';
import translations from 'src/views/pages/HomePage/components/Hero/.vocab';
import CsPhoneNumber from 'src/views/shared/CsPhoneNumber/CsPhoneNumber';
import HygraphRichText from 'src/views/shared/Hygraph/HygraphRichText';
import EmailCaptureFormContainer from 'src/views/shared/forms/EmailCaptureForm/EmailCaptureFormContainer';

import HeroImage from './images/hero-image-desktop.jpg';
import heroImageMobile from './images/hero-image-mobile.jpg';

import * as styles from './Hero.css';

const Hero = () => {
  const { site, isAnz, urlResolver } = useConfig();

  const landingPageContactUsContent = useHygraphRichText(
    SHOPFRONT_LANDING_PAGE_CONTACT_US,
  );

  const landingPageHelpCentreContent = useHygraphRichText(
    SHOPFRONT_LANDING_PAGE_HELP_CENTRE,
  );

  const { t } = useTranslations(translations);
  const id = 'hero-1';

  const AnzContactDetails = () => (
    <>
      <br />
      <CsPhoneNumber site={site} />
      <br />
      {CUSTOMER_SERVICE_HOURS[site]}
    </>
  );

  const NonAnzContactDetails = () => (
    <>
      <br />
      <TextLink
        href={urlResolver({
          path: EMPLOYER_CONTACT_US_URL[site],
        })}
        target="_blank"
      >
        Contact Us
      </TextLink>
      <br />
    </>
  );

  return (
    <Box id={id} background="surface">
      <ContentBlock>
        <Box
          paddingTop={['none', 'xlarge', 'xxlarge']}
          paddingBottom={['small', 'xlarge', 'xxlarge']}
        >
          <Hidden above="mobile">
            <img
              src={heroImageMobile}
              alt=""
              className={styles.heroImageMobile}
            />
          </Hidden>
          <Box paddingX={['xsmall', 'none']}>
            <Box className={styles.panelDetails} background="surface">
              <Columns space="none" collapseBelow="tablet">
                <Column>
                  <Box paddingX={['xsmall', 'large']} paddingY="large">
                    <Stack space="large">
                      <Heading level="1" component="h2">
                        {t('Headline')}
                      </Heading>
                      <Stack space="xlarge">
                        <Text size="large" tone="secondary">
                          {t('Headline description')}.
                        </Text>
                        <EmailCaptureFormContainer />
                      </Stack>
                      {landingPageContactUsContent &&
                      landingPageHelpCentreContent ? (
                        <Stack space="medium">
                          <HygraphRichText
                            textProps={{ tone: 'secondary' }}
                            richTextData={landingPageContactUsContent}
                          />
                          <HygraphRichText
                            textProps={{ tone: 'secondary', size: 'small' }}
                            richTextData={landingPageHelpCentreContent}
                          />
                        </Stack>
                      ) : (
                        <Stack space="medium">
                          <Text tone="secondary">
                            Get in touch with our local team
                            {isAnz ? (
                              <AnzContactDetails />
                            ) : (
                              <NonAnzContactDetails />
                            )}
                          </Text>
                          <Text tone="secondary" size="small">
                            Or visit our{' '}
                            <TextLink
                              href={urlResolver({
                                path: EMPLOYER_HELP_CENTRE_URL[site],
                              })}
                              target="_blank"
                            >
                              Help Centre
                            </TextLink>
                          </Text>
                        </Stack>
                      )}
                    </Stack>
                  </Box>
                </Column>
                <Column>
                  <Box
                    height="full"
                    marginRight={['none', 'large']}
                    position="relative"
                  >
                    <img
                      src={HeroImage}
                      alt=""
                      className={styles.heroImageDesktop}
                    />
                  </Box>
                </Column>
              </Columns>
            </Box>
          </Box>
        </Box>
      </ContentBlock>
    </Box>
  );
};

export default Hero;
